import { Search } from 'lucide-react';
import * as React from 'react';

import { Command as CommandPrimitive } from 'cmdk';

import { cn } from '../lib/utils';

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    // `darkGrey-100`/`cloudyBlue` is the SHARED default and stays put. It looks
    // like a one-off next to `bg-popover`/`text-neutral-300`, and a round of
    // token tidying did swap it — but this root is the body of ~19 popovers
    // outside any single feature (sidebar team switcher, currency rates,
    // Optimize, attribution assignment, every faceted filter, the metric
    // combobox, ...), and the swap drops their default body-text contrast from
    // 7.32:1 to 5.94:1 in one edit for no visual gain (#2A2B2E vs #2A2A32 is
    // one step of green/blue — invisible). Call sites that want a different ink
    // set it on their own items (see `labels/PortfolioScopeSelect.tsx`); do not
    // re-tokenise the shared default. Guarded by `Command.component.test.tsx`.
    className={cn('flex h-full w-full flex-col overflow-hidden rounded-lg bg-darkGrey-100 text-cloudyBlue', className)}
    {...props}
  />
));
Command.displayName = CommandPrimitive.displayName;

interface CommandInputProps extends React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> {
  borderColor?: string;
  /**
   * Show the magnifier glyph. Defaults to `true`, which is what every existing
   * call site already renders — opting OUT is the new behaviour, so the default
   * has to stay put.
   */
  searchIcon?: boolean;
  /**
   * Classes for the field's WRAPPER, not the input. The wrapper owns the border
   * and the padding, so a design that insets the field as its own bordered box
   * (rather than a full-bleed underlined row) has no other way to reach it.
   */
  wrapperClassName?: string;
}

const CommandInput = React.forwardRef<React.ElementRef<typeof CommandPrimitive.Input>, CommandInputProps>(
  ({ className, borderColor = 'border-neutral-700', searchIcon = true, wrapperClassName, ...props }, ref) => (
    // eslint-disable-next-line react/no-unknown-property
    <div className={cn('flex items-center border-b px-3', borderColor, wrapperClassName)} cmdk-input-wrapper="">
      {searchIcon ? <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" /> : null}
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          'flex h-11 w-full rounded-md bg-transparent py-3 text-sm font-normal outline-none placeholder:text-neutral-500 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
    </div>
  ),
);

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn('max-h-[300px] overflow-y-auto overflow-x-hidden', className)}
    {...props}
  />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => <CommandPrimitive.Empty ref={ref} className="py-6 text-center text-sm font-light" {...props} />);

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      'overflow-hidden p-1 text-cloudyBlue [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground',
      className,
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator ref={ref} className={cn('-mx-1 h-px bg-neutral-700', className)} {...props} />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm font-normal outline-none transition-all aria-selected:bg-accent-dark aria-selected:text-neutral-100 data-[disabled="true"]:pointer-events-none data-[disabled="true"]:opacity-50',
      className,
    )}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)} {...props} />;
};
CommandShortcut.displayName = 'CommandShortcut';

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
